import React, { createContext, useContext, useState } from 'react';
import Toast from '../components/Toast';
import { isNotificationsMuted } from '../utils/notificationPreferences';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 3000, title = '') => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration, title };
    setToasts((prev) => [...prev, newToast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // "Mute notifications" silences every non-error toast app-wide; errors always show.
  const unlessMuted = (type) => (message, duration, title) =>
    isNotificationsMuted() ? null : addToast(message, type, duration, title);

  const showSuccess = unlessMuted('success');
  const showError = (message, duration, title) => addToast(message, 'error', duration, title);
  const showWarning = unlessMuted('warning');
  const showInfo = unlessMuted('info');

  return (
    <ToastContext.Provider
      value={{
        addToast,
        removeToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          title={toast.title}
          type={toast.type}
          duration={toast.duration}
          stackIndex={index}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};
